import { useEffect, useMemo, useRef, useState } from "react";
import BidCustomers from "./BidCustomers";
import { BOARD_STATUS_CHIP, BoardStatusChip } from "./BidStatusChips";

// Pipeline statuses coloured with the Bid Board grid's palette (same chips, same meaning).
const PIPELINE_TO_BOARD = {
  Invitation: "Qualify", Estimating: "Started", "Estimate Complete": "Rebid", "Bid Submitted": "Submitted",
  "Budget Submitted": "Budget", Negotiating: "Reconcile", Won: "Won", Lost: "Lost", Cancelled: "Cancelled", "No Bid": "No Bid",
};
const statusChip = (st) => BOARD_STATUS_CHIP[PIPELINE_TO_BOARD[st] || st] || "";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CompanyPicker from "./CompanyPicker";
import ContactPicker from "./ContactPicker";
import EmailProposalDialog from "./EmailProposalDialog";
import WinJobDialog from "./WinJobDialog";
import { estApi } from "../lib/api";
import { useAutosave, AutosaveStatus } from "@/lib/useAutosave";

const inputCls = "mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs";
const REQUIREMENTS = [["prevailing_wage", "Prevailing wage"], ["bonded", "Bonded"],
  ["ocip_ccip", "OCIP / CCIP"], ["certified_payroll", "Certified payroll"],
  ["tax_exempt", "Tax exempt"], ["union", "Union"],
  ["badging_required", "Badging required"], ["drug_testing", "Drug testing"]];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

function Section({ title, hint, children, className = "", testId }) {
  return (
    <section className={`rounded-lg border border-slate-200 bg-white p-4 ${className}`} data-testid={testId}>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {hint && <p className="text-[11px] text-slate-400 mt-0.5 mb-3">{hint}</p>}
      <div className={hint ? "" : "mt-3"}>{children}</div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block min-w-0 ${className}`}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

export default function ProjectInfoTab({ estimate, statuses, onSaved, job = null, onJobChanged }) {
  const [form, setForm] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [people, setPeople] = useState([]);
  const [ams, setAms] = useState([]);
  const [emailCompany, setEmailCompany] = useState(null);
  const [winOpen, setWinOpen] = useState(false);
  const itbRef = useRef(null);
  // The whole invitation shows — the box takes the height of its own text, never a scrollbar.
  useEffect(() => {
    const el = itbRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [form?.itb_summary]);
  // No Save button — a field saves itself once you stop typing.
  const { status: saveStatus, trigger } = useAutosave(() => save());

  useEffect(() => {
    estApi.getCompanies({}).then((r) => setCompanies(r.companies)).catch(() => {});
    estApi.getContacts({}).then((r) => setContacts(r.contacts)).catch(() => {});
    estApi.getPeople().then((r) => {
      setPeople(r.people);
      setAms(r.account_managers || []);
    }).catch(() => {});
  }, []);

  // Sales stores the AM as initials — show the full name here, like the estimator.
  const amFull = useMemo(() => {
    const byInitials = {};
    ams.forEach((a) => { if (a.initials) byInitials[a.initials.toUpperCase()] = a.name; });
    return (v) => byInitials[(v || "").trim().toUpperCase()] || v || "";
  }, [ams]);

  useEffect(() => {
    if (!estimate) return;
    setForm({
      project_name: estimate.project_name || "",
      city: estimate.city || "",
      state: estimate.state || "",
      status: estimate.status,
      gc_company_ids: estimate.gc_company_ids || [],
      contacts: (estimate.contact_details || estimate.contacts || []).map((c) => ({
        id: c.id, contact_id: c.contact_id, role: c.role, is_email_recipient: c.is_email_recipient,
      })),
      account_manager_name: amFull(estimate.account_manager_name),
      estimator_name: estimate.estimator_name || "",
      itb_summary: estimate.itb_summary || "",
      bid_due_date: estimate.bid_due_date || "",
      requirements: estimate.requirements || {},
    });
    // Deliberately keyed on the estimate id only: the account-manager list arrives later and
    // re-running this effect would throw away anything typed in the meantime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate?.id]);

  // Once the AM list lands, swap initials for the full name — but only if nobody has typed.
  useEffect(() => {
    setForm((f) => (f && f.account_manager_name === (estimate?.account_manager_name || "")
      ? { ...f, account_manager_name: amFull(estimate?.account_manager_name) } : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amFull]);

  if (!form) return null;
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); trigger(); };


  const save = async () => {
    if (!form.project_name.trim()) return toast.error("Job name is required");
    try {
      // On a bid the customers are saved as they're picked (shared with the board), not here.
      const { gc_company_ids, contacts, ...rest } = form;
      const saved = await estApi.updateEstimate(estimate.id, {
        ...(job ? rest : form),
        account_manager_email: people.find((p) => p.name === form.account_manager_name)?.email || "",
        estimator_email: people.find((p) => p.name === form.estimator_name)?.email || "",
      });
      onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const recipients = form.contacts.filter((c) => c.is_email_recipient).length;
  const frozen = !!estimate.ops_frozen;

  return (
    <div className="space-y-4 pb-8" data-testid="project-info-tab">
      {frozen && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800"
           data-testid="info-frozen-banner">
          This is the original estimate as sold — it is frozen. Make changes on the working
          (as-built) estimate instead.
        </p>
      )}
      <WinJobDialog open={winOpen} onOpenChange={setWinOpen} estimateId={estimate.id}
                    onWon={() => onSaved?.()} />
      <fieldset disabled={frozen}
                className={`space-y-4 xl:grid xl:grid-cols-2 xl:items-stretch xl:gap-4 xl:space-y-0 ${
                  frozen ? "pointer-events-none opacity-60" : ""}`}>
      <Section title="Project" className="xl:col-start-1 xl:row-start-1" testId="info-section-project">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Job name" className="md:col-span-2">
            <input value={form.project_name} onChange={(e) => set("project_name", e.target.value)}
                   className={inputCls} data-testid="info-project-name" />
          </Field>
          <Field label="City">
            <input value={form.city} onChange={(e) => set("city", e.target.value)}
                   className={inputCls} data-testid="info-city" />
          </Field>
          <Field label="State">
            <select value={form.state} onChange={(e) => set("state", e.target.value)}
                    className={inputCls} data-testid="info-state">
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Status">
            {job ? (
              // One lifecycle, one setter: the same Board status chip as the bid header and the
              // Bid Board grid, so a change here is the change everywhere.
              <div className="flex h-8 items-center" data-testid="info-status-chip">
                <BoardStatusChip job={job} canEdit={!frozen} onChanged={onJobChanged} />
              </div>
            ) : (
            <select value={form.status} data-testid="info-status"
                    className={`${inputCls} border font-medium ${statusChip(form.status)}`}
                    onChange={(e) => {
                      // Won is a hand-off: the AM says which scopes were awarded first, so the
                      // status is set by that dialog and not by this autosave.
                      if (e.target.value === "Won" && estimate.status !== "Won"
                          && !estimate.ops_project_id) { setWinOpen(true); return; }
                      set("status", e.target.value);
                    }}>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            )}
          </Field>
          <Field label="Bid due">
            <input type="date" value={form.bid_due_date || ""}
                   onChange={(e) => set("bid_due_date", e.target.value)}
                   className={inputCls} data-testid="info-bid-due" />
          </Field>
        </div>
      </Section>

      {job ? (
        <Section title="Customer(s)" className="flex flex-col xl:col-start-2 xl:row-start-1" testId="info-section-customers">
          <BidCustomers job={job} estimateId={estimate.id} canEdit={!frozen} onChanged={onJobChanged} />
        </Section>
      ) : (
      <Section title="Add customer" className="xl:col-start-2 xl:row-start-1" testId="info-section-customers"
               hint="The customer and its contact in one place. More than one GC on the same bid is
                     normal — each gets its own proposal email, addressed to its own contact.">
        {!form.gc_company_ids.length && estimate.gc_name && (
          <p className="mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700"
             data-testid="legacy-gc-name-hint">
            This estimate carries "{estimate.gc_name}" as plain text from before customers were
            linked records. Search for the matching company below so contacts and the proposal
            address fill in.
          </p>
        )}
        <CompanyPicker
          value={form.gc_company_ids} companies={companies}
          onChange={(ids) => set("gc_company_ids", ids)}
          onEmail={(c) => setEmailCompany(c)}
          onCompanyAdded={(c) => setCompanies((cs) => (cs.some((x) => x.id === c.id) ? cs
            : [...cs, c].sort((a, b) => a.name.localeCompare(b.name))))}
        />

        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="mb-2 flex items-baseline gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Contacts
            </span>
            <span className="text-[10px] text-slate-400">
              {form.gc_company_ids.length
                ? `${recipients} marked to receive the proposal`
                : "Add a customer first, or attach any contact"}
            </span>
          </div>
          <ContactPicker
            links={form.contacts} contacts={contacts} companies={companies}
            companyIds={form.gc_company_ids}
            onChange={(links) => set("contacts", links)}
            onContactAdded={(c) => setContacts((cs) => [...cs, c])}
            onContactUpdated={(c) => setContacts((cs) => cs.map((x) => (x.id === c.id ? c : x)))}
            onEmail={(c) => setEmailCompany({ id: c.company_id || "", name: c.display_name || c.email, emails: [c.email] })}
          />
        </div>
      </Section>
      )}

      <Section title="Requirements" className="xl:col-start-2 xl:row-start-2" testId="info-section-requirements">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {REQUIREMENTS.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={!!form.requirements?.[key]}
                     onChange={(e) => set("requirements",
                       { ...(form.requirements || {}), [key]: e.target.checked })}
                     data-testid={`info-req-${key}`} />
              {label}
            </label>
          ))}
        </div>
      </Section>

      <Section title="ITB summary" className="xl:col-start-1 xl:row-start-2" testId="info-section-itb">
        <textarea ref={itbRef} value={form.itb_summary} rows={5}
                  onChange={(e) => set("itb_summary", e.target.value)}
                  className="w-full resize-y overflow-hidden rounded-md border border-slate-200 px-2 py-1.5 text-xs leading-relaxed"
                  data-testid="info-itb-summary" />
      </Section>

      </fieldset>

      <div className="sticky bottom-0 -mx-1 flex items-center justify-end gap-3 border-t border-slate-200 bg-[#F8FAFC]/95 px-1 py-3 backdrop-blur">
        <AutosaveStatus status={saveStatus} />
      </div>

      {emailCompany && (
        <EmailProposalDialog
          open onOpenChange={(v) => !v && setEmailCompany(null)}
          estimateId={estimate.id}
          restrictEmails={emailCompany.emails || contacts.filter((c) => c.company_id === emailCompany.id && c.email)
            .map((c) => c.email)}
          restrictLabel={emailCompany.name}
          companyId={emailCompany.id}
          onSent={() => setEmailCompany(null)}
        />
      )}
    </div>
  );
}
